// McpToolProjections — 管理本單位的「MCP 工具可見性覆蓋」。
//
// 列出本客戶單位對外部 AI 透過 MCP 看得到 / 叫得動哪些工具的覆蓋設定；可
// 新增 / 編輯 / 刪除。每筆覆蓋只能「收緊」：把某個工具對外部 AI 隱藏，或設
// 成「只有符合特定角色 / 權限情境的呼叫者才看得到、叫得動」。覆蓋永遠不會
// 「放寬」——它絕不會讓人看到本來就無權使用的工具；它頂多把呼叫者本來就有
// 權限用的工具再藏起來。設定同時影響外部 AI 能「列出」與「呼叫」哪些工具。
//
// 鏡像對象：apps/frontend/src/dashboard/pages/McpApprovals.tsx（PageHeader +
// loading/error/empty/ready 四態 + ConfirmDialog + friendly error mapping +
// data-tour-ids + dark theme）。後端：apps/runtime-api/src/index.ts
// /v1/runtime/mcp-tool-projections（CRUD，需 mcp:manage_projection 權限）。
// Spec：docs/specs/nexus-mcp-tool-projection-policy-v0.md。
//
// 條件（context_gated）的判斷語言與 @nexus/shared mcp-projection 的 Predicate
// 共用同一套運算子；前端做基本檢查，後端用同一個 validatePredicate 再驗一次
// （防禦性深度防護），後端的驗證錯誤會被友善地呈現出來。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  ServerCrash,
  EyeOff,
  Eye,
  Filter,
  Plus,
  Pencil,
  Trash2,
  ShieldQuestion,
  X,
} from "lucide-react";
// Import the canonical tool-name set from the D1-free subpath (not the
// mcp-projection index, whose store.ts pulls in Cloudflare `D1Database` globals
// the frontend tsconfig deliberately does not load).
import { MCP_PROJECTABLE_TOOL_NAMES } from "@nexus/shared/mcp-projection/tool-names";
import { ROLES, type Role } from "@nexus/shared/rbac";
import { roleLabel } from "@nexus/shared/labels";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { formatTaipeiDateTime } from "../../lib/time.js";
import { PERMISSION_CATALOG } from "../lib/permissionCatalog.js";
import {
  listMcpToolProjections,
  createMcpToolProjection,
  updateMcpToolProjection,
  deleteMcpToolProjection,
  friendlyMcpProjectionError,
  type ProjectionRow,
  type ProjectionVisibility,
  type ProjectionLifecycle,
  type Predicate,
  type PredicateEnvironment,
} from "../lib/mcpToolProjectionsApi.js";

// Tenant-assignable roles only (the predicate is evaluated against a tenant
// caller's resolved role; super_admin is platform-scoped god mode and is not a
// meaningful gate target). Sorted, labelled.
const TENANT_ROLES: Role[] = ROLES.filter((r) => r !== "super_admin");

// Permission options for the has_permission / has_any_permission ops — the same
// hand-curated catalog the role editor uses (label_zh for readable rendering).
const PERMISSION_OPTIONS = PERMISSION_CATALOG.map((p) => ({ id: p.id, label: p.label_zh }));

const TOOL_NAMES: string[] = [...MCP_PROJECTABLE_TOOL_NAMES].sort((a, b) => a.localeCompare(b));

// The deploy environments the `environment_in` leaf gates on. Order is the
// natural promotion path DEV → UAT → PROD; labels are plain-language (the var is
// the MCP server's own deploy environment, NOT the caller's identity).
const ENVIRONMENTS: { id: PredicateEnvironment; label: string }[] = [
  { id: "DEV", label: "開發（DEV）" },
  { id: "UAT", label: "測試（UAT）" },
  { id: "PROD", label: "正式（PROD）" },
];

function envLabel(id: string): string {
  return ENVIRONMENTS.find((e) => e.id === id)?.label ?? id;
}

const VISIBILITY_LABEL: Record<ProjectionVisibility, string> = {
  visible: "顯示",
  hidden: "隱藏",
  context_gated: "依情境顯示",
};

type ListState =
  | { status: "loading" }
  | { status: "ready"; items: ProjectionRow[] }
  | { status: "error"; message: string };

// ── A leaf predicate node the builder can emit (no nesting). all/any wrap these. ─
type LeafOp = "role_in" | "has_permission" | "has_any_permission" | "environment_in";
type GroupOp = "all" | "any";

export default function McpToolProjections(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const { confirm, alert } = useConfirmDialog();

  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editor, setEditor] = useState<{ mode: "create" } | { mode: "edit"; row: ProjectionRow } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listMcpToolProjections({ tenantId: tenantParam, limit: 500 });
      setState({ status: "ready", items: r.projections });
    } catch (err) {
      setState({ status: "error", message: friendlyMcpProjectionError(err) });
    }
  }, [tenantParam]);

  useEffect(() => {
    void load();
  }, [load]);

  const existingToolNames = useMemo(
    () => (state.status === "ready" ? new Set(state.items.map((r) => r.tool_name)) : new Set<string>()),
    [state],
  );

  const remove = useCallback(
    async (row: ProjectionRow) => {
      const ok = await confirm(
        `確定要刪除「${row.tool_name}」的可見性覆蓋嗎？\n\n刪除後，這個工具會回到「只看權限」的預設狀態——也就是不再額外隱藏或加情境條件。此操作會記錄到操作紀錄。`,
        { title: "刪除 MCP 工具可見性覆蓋", okLabel: "刪除", cancelLabel: "取消", destructive: true },
      );
      if (!ok) return;
      setBusyId(row.id);
      try {
        await deleteMcpToolProjection(row.id, tenantParam);
        await load();
      } catch (err) {
        await alert(friendlyMcpProjectionError(err), { title: "刪除失敗", okLabel: "知道了" });
      } finally {
        setBusyId(null);
      }
    },
    [confirm, tenantParam, load],
  );

  const superAdminNoTenant = session.super_admin && !tenantParam;

  return (
    <>
      <PageHeader
        title="MCP 工具可見性"
        description={
          superAdminNoTenant
            ? "您是平台管理員。請先選定一個客戶單位（進入該單位身分）才能管理其 MCP 工具可見性覆蓋。"
            : "控制外部 AI 透過 MCP 看得到、叫得動哪些工具。每筆覆蓋只能「收緊」：把工具隱藏，或設成「只有符合特定角色 / 權限情境才看得到」。覆蓋永遠不會放寬權限——絕不會讓人用到本來就無權使用的工具，只會把已有權限的工具再藏起來。設定同時影響外部 AI 能「列出」與「呼叫」哪些工具。"
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary text-sm"
              onClick={() => void load()}
              disabled={state.status === "loading"}
              data-tour-id="mcp-tool-projections.refresh"
            >
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            <button
              className="btn-primary text-sm"
              onClick={() => setEditor({ mode: "create" })}
              disabled={superAdminNoTenant}
              data-tour-id="mcp-tool-projections.add"
            >
              <Plus className="h-4 w-4" /> 新增覆蓋
            </button>
          </div>
        }
      />

      {state.status === "loading" && (
        <div className="card p-5 space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="可見性覆蓋載入失敗"
          description={state.message}
          action={
            <button className="btn-primary text-sm" onClick={() => void load()}>
              再試一次
            </button>
          }
        />
      )}

      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState
          icon={ShieldQuestion}
          title="目前沒有任何 MCP 工具可見性覆蓋"
          description="沒有覆蓋時，外部 AI 看得到、叫得動的工具完全由呼叫者的權限決定。新增覆蓋可以把某個工具對外部 AI 隱藏，或設成只有特定情境才顯示。"
          action={
            <button className="btn-primary text-sm" onClick={() => setEditor({ mode: "create" })}>
              <Plus className="h-4 w-4" /> 新增覆蓋
            </button>
          }
        />
      )}

      {state.status === "ready" && state.items.length > 0 && (
        <div className="card divide-y divide-border" data-tour-id="mcp-tool-projections.list">
          {state.items.map((row) => (
            <ProjectionRowView
              key={row.id}
              row={row}
              busy={busyId === row.id}
              onEdit={() => setEditor({ mode: "edit", row })}
              onDelete={() => void remove(row)}
            />
          ))}
        </div>
      )}

      {editor && (
        <ProjectionEditor
          mode={editor.mode}
          row={editor.mode === "edit" ? editor.row : null}
          tenantParam={tenantParam}
          existingToolNames={existingToolNames}
          onClose={() => setEditor(null)}
          onSaved={async () => {
            setEditor(null);
            await load();
          }}
        />
      )}
    </>
  );
}

function VisibilityBadge({ visibility }: { visibility: ProjectionVisibility }): React.ReactElement {
  if (visibility === "hidden")
    return (
      <span className="badge-danger inline-flex items-center gap-1 text-xs">
        <EyeOff className="h-3 w-3" /> {VISIBILITY_LABEL.hidden}
      </span>
    );
  if (visibility === "context_gated")
    return (
      <span className="badge-warning inline-flex items-center gap-1 text-xs">
        <Filter className="h-3 w-3" /> {VISIBILITY_LABEL.context_gated}
      </span>
    );
  return (
    <span className="badge-success inline-flex items-center gap-1 text-xs">
      <Eye className="h-3 w-3" /> {VISIBILITY_LABEL.visible}
    </span>
  );
}

/** Plain-language one-line summary of a predicate, for the list view. */
function predicateSummary(p: Predicate | null): string {
  if (!p) return "—";
  return describe(p);
}

function describe(p: Predicate): string {
  switch (p.op) {
    case "role_in":
      return `角色屬於：${p.roles.map((r) => roleLabel(r)).join("、") || "（未設定）"}`;
    case "has_permission":
      return `具備權限：${permLabel(p.permission)}`;
    case "has_any_permission":
      return `具備任一權限：${p.permissions.map(permLabel).join("、") || "（未設定）"}`;
    case "environment_in":
      return `執行環境屬於：${p.environments.map(envLabel).join("、") || "（未設定）"}`;
    case "all":
      return `同時滿足（${p.of.length}）：${p.of.map(describe).join("；")}`;
    case "any":
      return `滿足任一（${p.of.length}）：${p.of.map(describe).join("；")}`;
    default:
      return "（未知條件）";
  }
}

function permLabel(id: string): string {
  return PERMISSION_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

function ProjectionRowView({
  row,
  busy,
  onEdit,
  onDelete,
}: {
  row: ProjectionRow;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): React.ReactElement {
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-medium text-text-primary truncate">{row.tool_name}</span>
            <VisibilityBadge visibility={row.visibility} />
            {row.lifecycle === "inactive" && (
              <span className="badge-default text-xs">未啟用</span>
            )}
          </div>

          {row.visibility === "context_gated" && (
            <div className="mt-1.5 text-xs text-text-secondary">
              條件：<span className="text-text-primary">{predicateSummary(row.context_predicate)}</span>
            </div>
          )}

          <div className="mt-1.5 text-2xs text-text-muted font-mono">
            最後更新 {formatTaipeiDateTime(row.updated_at)}
          </div>
        </div>

        <div className="flex shrink-0 flex-row items-center gap-2">
          <button onClick={onEdit} className="btn-secondary text-sm" disabled={busy}>
            <Pencil className="h-4 w-4" /> 編輯
          </button>
          <button onClick={onDelete} className="btn-danger text-sm" disabled={busy}>
            <Trash2 className="h-4 w-4" /> 刪除
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The create / edit modal + predicate builder ──────────────────────────────

/** Editor-local predicate node model. A node is either a single leaf condition
 *  or a one-level group (all/any) of leaf conditions. This matches "you may
 *  support one level of all/any nesting" — emitting valid predicate JSON the
 *  server re-validates with the shared validatePredicate. */
type LeafNode =
  | { kind: "role_in"; roles: string[] }
  | { kind: "has_permission"; permission: string }
  | { kind: "has_any_permission"; permissions: string[] }
  | { kind: "environment_in"; environments: PredicateEnvironment[] };

type BuilderModel =
  | { mode: "leaf"; leaf: LeafNode }
  | { mode: "group"; op: GroupOp; children: LeafNode[] };

function emptyLeaf(op: LeafOp): LeafNode {
  if (op === "role_in") return { kind: "role_in", roles: [] };
  if (op === "has_permission") return { kind: "has_permission", permission: "" };
  if (op === "environment_in") return { kind: "environment_in", environments: [] };
  return { kind: "has_any_permission", permissions: [] };
}

/** Convert a stored predicate into the editor model (best-effort; a shape the
 *  builder can't represent — e.g. nested groups inside groups — falls back to a
 *  single empty role_in leaf so the admin can re-author it). */
function predicateToModel(p: Predicate | null): BuilderModel {
  if (!p) return { mode: "leaf", leaf: emptyLeaf("role_in") };
  if (p.op === "all" || p.op === "any") {
    const children: LeafNode[] = [];
    for (const child of p.of) {
      const leaf = predicateToLeaf(child);
      if (leaf) children.push(leaf);
    }
    return { mode: "group", op: p.op, children: children.length ? children : [emptyLeaf("role_in")] };
  }
  const leaf = predicateToLeaf(p);
  return { mode: "leaf", leaf: leaf ?? emptyLeaf("role_in") };
}

function predicateToLeaf(p: Predicate): LeafNode | null {
  if (p.op === "role_in") return { kind: "role_in", roles: [...p.roles] };
  if (p.op === "has_permission") return { kind: "has_permission", permission: p.permission };
  if (p.op === "has_any_permission") return { kind: "has_any_permission", permissions: [...p.permissions] };
  if (p.op === "environment_in") return { kind: "environment_in", environments: [...p.environments] };
  return null; // nested group inside group — not representable in this builder
}

function leafToPredicate(leaf: LeafNode): Predicate {
  if (leaf.kind === "role_in") return { op: "role_in", roles: leaf.roles };
  if (leaf.kind === "has_permission") return { op: "has_permission", permission: leaf.permission };
  if (leaf.kind === "environment_in") return { op: "environment_in", environments: leaf.environments };
  return { op: "has_any_permission", permissions: leaf.permissions };
}

function modelToPredicate(m: BuilderModel): Predicate {
  if (m.mode === "leaf") return leafToPredicate(m.leaf);
  return { op: m.op, of: m.children.map(leafToPredicate) };
}

/** Client-side completeness check mirroring the shared validator's leaf rules
 *  (non-empty roles / permission / permissions). Returns an error message or
 *  null. The server re-validates with validatePredicate (defense-in-depth). */
function validateLeaf(leaf: LeafNode): string | null {
  if (leaf.kind === "role_in" && leaf.roles.length === 0) return "「角色屬於」至少要選一個角色。";
  if (leaf.kind === "has_permission" && !leaf.permission) return "「具備權限」要選一個權限。";
  if (leaf.kind === "has_any_permission" && leaf.permissions.length === 0)
    return "「具備任一權限」至少要選一個權限。";
  if (leaf.kind === "environment_in" && leaf.environments.length === 0)
    return "「執行環境屬於」至少要選一個環境。";
  return null;
}

function validateModel(m: BuilderModel): string | null {
  if (m.mode === "leaf") return validateLeaf(m.leaf);
  if (m.children.length === 0) return "群組裡至少要有一個條件。";
  for (const c of m.children) {
    const e = validateLeaf(c);
    if (e) return e;
  }
  return null;
}

function ProjectionEditor({
  mode,
  row,
  tenantParam,
  existingToolNames,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  row: ProjectionRow | null;
  tenantParam: string | null;
  existingToolNames: Set<string>;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}): React.ReactElement {
  const [toolName, setToolName] = useState<string>(row?.tool_name ?? "");
  const [visibility, setVisibility] = useState<ProjectionVisibility>(row?.visibility ?? "hidden");
  const [lifecycle, setLifecycle] = useState<ProjectionLifecycle>(row?.lifecycle ?? "active");
  const [builder, setBuilder] = useState<BuilderModel>(() => predicateToModel(row?.context_predicate ?? null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For create: only offer tools that don't already have an override (one per
  // tool per tenant — the server enforces UNIQUE and returns 409 otherwise).
  const toolOptions = useMemo(
    () => (mode === "create" ? TOOL_NAMES.filter((t) => !existingToolNames.has(t)) : TOOL_NAMES),
    [mode, existingToolNames],
  );

  const save = useCallback(async () => {
    setError(null);
    if (mode === "create" && !toolName) {
      setError("請先選擇一個工具。");
      return;
    }
    let predicate: Predicate | undefined;
    if (visibility === "context_gated") {
      const e = validateModel(builder);
      if (e) {
        setError(e);
        return;
      }
      predicate = modelToPredicate(builder);
    }
    setSaving(true);
    try {
      if (mode === "create") {
        await createMcpToolProjection(
          { tool_name: toolName, visibility, lifecycle, ...(predicate ? { context_predicate: predicate } : {}) },
          tenantParam,
        );
      } else if (row) {
        await updateMcpToolProjection(
          row.id,
          { visibility, lifecycle, ...(predicate ? { context_predicate: predicate } : {}) },
          tenantParam,
        );
      }
      await onSaved();
    } catch (err) {
      setError(friendlyMcpProjectionError(err));
    } finally {
      setSaving(false);
    }
  }, [mode, toolName, visibility, lifecycle, builder, row, tenantParam, onSaved]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-5" data-tour-id="mcp-tool-projections.editor">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-text-primary">
            {mode === "create" ? "新增 MCP 工具可見性覆蓋" : "編輯 MCP 工具可見性覆蓋"}
          </h2>
          <button className="text-text-muted hover:text-text-primary" onClick={onClose} disabled={saving} aria-label="關閉">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 rounded border border-border bg-bg-raised px-3 py-2 text-xs text-text-secondary">
          覆蓋只能「收緊」：絕不會讓外部 AI 用到呼叫者本來就無權使用的工具，只會把已有權限的工具再隱藏或加上情境條件。設定同時影響外部 AI 能「列出」與「呼叫」哪些工具。
        </p>

        {/* Tool picker (immutable once created). */}
        <label className="mb-1 block text-sm font-medium text-text-primary">工具</label>
        {mode === "create" ? (
          <select
            className="input mb-4 w-full"
            value={toolName}
            onChange={(e) => setToolName(e.target.value)}
            disabled={saving}
            data-tour-id="mcp-tool-projections.tool-select"
          >
            <option value="">— 請選擇工具 —</option>
            {toolOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        ) : (
          <div className="mb-4 font-mono text-sm text-text-primary">{toolName}</div>
        )}

        {/* Visibility. */}
        <label className="mb-1 block text-sm font-medium text-text-primary">可見性</label>
        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {(["hidden", "context_gated", "visible"] as ProjectionVisibility[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVisibility(v)}
              disabled={saving}
              className={`rounded border px-3 py-2 text-left text-sm transition ${
                visibility === v
                  ? "border-accent bg-accent/10 text-text-primary"
                  : "border-border bg-bg-raised text-text-secondary hover:border-accent/50"
              }`}
            >
              <div className="font-medium">{VISIBILITY_LABEL[v]}</div>
              <div className="mt-0.5 text-2xs text-text-muted">
                {v === "hidden"
                  ? "永遠對外部 AI 隱藏"
                  : v === "context_gated"
                    ? "只有符合條件的呼叫者看得到"
                    : "回到只看權限的預設（等同沒有覆蓋）"}
              </div>
            </button>
          ))}
        </div>

        {/* Predicate builder — only for context_gated. */}
        {visibility === "context_gated" && (
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-text-primary">顯示條件</label>
            <p className="mb-2 text-2xs text-text-muted">
              只有「滿足條件」的呼叫者才看得到、叫得動這個工具。條件用呼叫者的角色 / 權限來判斷。
            </p>
            <PredicateBuilder model={builder} onChange={setBuilder} disabled={saving} />
          </div>
        )}

        {/* Lifecycle. */}
        <label className="mb-1 block text-sm font-medium text-text-primary">啟用狀態</label>
        <div className="mb-4 flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="radio"
              name="lifecycle"
              checked={lifecycle === "active"}
              onChange={() => setLifecycle("active")}
              disabled={saving}
            />
            啟用（立即生效）
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="radio"
              name="lifecycle"
              checked={lifecycle === "inactive"}
              onChange={() => setLifecycle("inactive")}
              disabled={saving}
            />
            未啟用（暫停此覆蓋，工具回到只看權限）
          </label>
        </div>

        {error && (
          <div className="mb-4 rounded border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button className="btn-secondary text-sm" onClick={onClose} disabled={saving}>
            取消
          </button>
          <button className="btn-primary text-sm" onClick={() => void save()} disabled={saving}>
            {saving ? "儲存中…" : mode === "create" ? "新增" : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The structured predicate builder ─────────────────────────────────────────

function PredicateBuilder({
  model,
  onChange,
  disabled,
}: {
  model: BuilderModel;
  onChange: (m: BuilderModel) => void;
  disabled: boolean;
}): React.ReactElement {
  // Switch between a single condition (leaf) and a group (all/any of leaves).
  const isGroup = model.mode === "group";

  const setStructure = (next: "leaf" | "all" | "any") => {
    if (next === "leaf") {
      // Keep the first leaf if coming from a group.
      const first = model.mode === "group" ? model.children[0] ?? emptyLeaf("role_in") : model.leaf;
      onChange({ mode: "leaf", leaf: first });
    } else {
      const children = model.mode === "group" ? model.children : [model.leaf];
      onChange({ mode: "group", op: next, children: children.length ? children : [emptyLeaf("role_in")] });
    }
  };

  return (
    <div className="rounded border border-border bg-bg-raised p-3">
      {/* Structure selector. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-text-muted">條件結構：</span>
        {([
          ["leaf", "單一條件"],
          ["all", "全部滿足（且）"],
          ["any", "滿足任一（或）"],
        ] as const).map(([k, label]) => {
          const active = k === "leaf" ? !isGroup : isGroup && model.op === k;
          return (
            <button
              key={k}
              type="button"
              disabled={disabled}
              onClick={() => setStructure(k)}
              className={`rounded px-2 py-1 ${
                active ? "bg-accent/20 text-text-primary" : "bg-bg-base text-text-secondary hover:text-text-primary"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {model.mode === "leaf" ? (
        <LeafEditor
          leaf={model.leaf}
          disabled={disabled}
          onChange={(leaf) => onChange({ mode: "leaf", leaf })}
        />
      ) : (
        <div className="space-y-2">
          {model.children.map((child, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="flex-1">
                <LeafEditor
                  leaf={child}
                  disabled={disabled}
                  onChange={(leaf) => {
                    const children = model.children.slice();
                    children[i] = leaf;
                    onChange({ ...model, children });
                  }}
                />
              </div>
              <button
                type="button"
                className="mt-1 text-text-muted hover:text-danger"
                disabled={disabled || model.children.length <= 1}
                title={model.children.length <= 1 ? "群組至少要保留一個條件" : "移除這個條件"}
                onClick={() => {
                  const children = model.children.filter((_, idx) => idx !== i);
                  onChange({ ...model, children: children.length ? children : [emptyLeaf("role_in")] });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={disabled}
            onClick={() => onChange({ ...model, children: [...model.children, emptyLeaf("role_in")] })}
          >
            <Plus className="h-3.5 w-3.5" /> 新增條件
          </button>
        </div>
      )}
    </div>
  );
}

function LeafEditor({
  leaf,
  onChange,
  disabled,
}: {
  leaf: LeafNode;
  onChange: (leaf: LeafNode) => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <div className="rounded border border-border bg-bg-base p-2">
      <select
        className="input mb-2 w-full text-sm"
        value={leaf.kind}
        disabled={disabled}
        onChange={(e) => onChange(emptyLeaf(e.target.value as LeafOp))}
      >
        <option value="role_in">角色屬於（其中之一）</option>
        <option value="has_permission">具備某個權限</option>
        <option value="has_any_permission">具備任一權限</option>
        <option value="environment_in">執行環境屬於（其中之一）</option>
      </select>

      {leaf.kind === "role_in" && (
        <div className="flex flex-wrap gap-1.5">
          {TENANT_ROLES.map((r) => {
            const checked = leaf.roles.includes(r);
            return (
              <button
                key={r}
                type="button"
                disabled={disabled}
                onClick={() =>
                  onChange({
                    kind: "role_in",
                    roles: checked ? leaf.roles.filter((x) => x !== r) : [...leaf.roles, r],
                  })
                }
                className={`rounded border px-2 py-1 text-xs ${
                  checked
                    ? "border-accent bg-accent/10 text-text-primary"
                    : "border-border bg-bg-raised text-text-secondary hover:border-accent/50"
                }`}
              >
                {roleLabel(r)}
              </button>
            );
          })}
        </div>
      )}

      {leaf.kind === "has_permission" && (
        <select
          className="input w-full text-sm"
          value={leaf.permission}
          disabled={disabled}
          onChange={(e) => onChange({ kind: "has_permission", permission: e.target.value })}
        >
          <option value="">— 選擇權限 —</option>
          {PERMISSION_OPTIONS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}（{p.id}）
            </option>
          ))}
        </select>
      )}

      {leaf.kind === "has_any_permission" && (
        <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
          {PERMISSION_OPTIONS.map((p) => {
            const checked = leaf.permissions.includes(p.id);
            return (
              <label key={p.id} className="flex items-center gap-2 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() =>
                    onChange({
                      kind: "has_any_permission",
                      permissions: checked
                        ? leaf.permissions.filter((x) => x !== p.id)
                        : [...leaf.permissions, p.id],
                    })
                  }
                />
                <span>
                  {p.label} <span className="font-mono text-text-muted">（{p.id}）</span>
                </span>
              </label>
            );
          })}
        </div>
      )}

      {leaf.kind === "environment_in" && (
        <>
          <p className="mb-1.5 text-2xs text-text-muted">
            依「MCP 伺服器目前執行的環境」判斷（開發 / 測試 / 正式），不是依呼叫者身分。例如可把危險工具設成只在開發 / 測試環境顯示，正式環境隱藏。若系統一時無法確認執行環境，這個條件會「保守地不顯示」工具。
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ENVIRONMENTS.map((e) => {
              const checked = leaf.environments.includes(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onChange({
                      kind: "environment_in",
                      environments: checked
                        ? leaf.environments.filter((x) => x !== e.id)
                        : [...leaf.environments, e.id],
                    })
                  }
                  className={`rounded border px-2 py-1 text-xs ${
                    checked
                      ? "border-accent bg-accent/10 text-text-primary"
                      : "border-border bg-bg-raised text-text-secondary hover:border-accent/50"
                  }`}
                >
                  {e.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
